import { ref } from 'vue';
import { readWebsiteNameFromStorage } from '@/utils/currentWebsite';
import { countries as phoneCountries } from '@/utils/countries';

const defaultCountrySlug = 'ca';

export function readDefaultCountrySlugFromStorage() {
  const fromStorage = (localStorage.getItem('default_country_slug') || '').toLowerCase().trim();
  return fromStorage || defaultCountrySlug;
}

export function setDefaultCountrySlug(slug) {
  const s = (slug || '').toLowerCase().trim();
  if (s) localStorage.setItem('default_country_slug', s);
}

export function readCountrySlugFromStorage() {
  const fromStorage = (localStorage.getItem('country_slug') || '').toLowerCase().trim();
  return fromStorage || defaultCountrySlug;
}

export const currentCountrySlug = ref(readCountrySlugFromStorage());

export function setCurrentCountrySlug(slug) {
  const s = (slug || '').toLowerCase().trim();
  if (s) {
    currentCountrySlug.value = s;
    localStorage.setItem('country_slug', s);
  }
}

export function readCountryNameFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
    const name = (stored?.name || '').toString().trim();
    return name;
  } catch {
    return '';
  }
}

export function readSelectedCountryFromStorage() {
  try {
    return JSON.parse(localStorage.getItem('selected_country') || 'null');
  } catch {
    return null;
  }
}

export function readCountriesCache() {
  try {
    const cached = JSON.parse(localStorage.getItem('countries_cache') || '[]');
    return Array.isArray(cached) ? cached : [];
  } catch {
    return [];
  }
}

/** Website name for the active country slug (from countries API cache). */
export function resolveWebsiteNameForSelectedCountry(fallbackWebsiteName = '') {
  const slug = (currentCountrySlug.value || readCountrySlugFromStorage() || '').toLowerCase().trim();
  if (slug) {
    const country = readCountriesCache().find(
      (item) => String(item?.slug || '').toLowerCase() === slug
    );
    const websiteName = (country?.website?.name || '').toString().trim();
    if (websiteName) return websiteName;
  }

  const fallback = (fallbackWebsiteName || readWebsiteNameFromStorage() || '').toString().trim();
  return fallback;
}

export function readSelectedCountryCodeFromStorage() {
  try {
    const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
    return (stored?.code || '').toString().trim().toUpperCase();
  } catch {
    return '';
  }
}

export function findPhoneCountryByCode(code) {
  const normalized = (code || '').toString().trim().toUpperCase();
  if (!normalized) return null;
  return phoneCountries.find((item) => item.code === normalized) || null;
}

/** Dial-code metadata for the navbar-selected country (ISO code from API / localStorage). */
export function resolvePhoneCountryForSelection(countryRecord, fallbackCode = 'AE') {
  const code = (
    countryRecord?.code
    || readSelectedCountryCodeFromStorage()
    || fallbackCode
  ).toString().trim().toUpperCase();

  return findPhoneCountryByCode(code)
    || findPhoneCountryByCode(fallbackCode)
    || findPhoneCountryByCode('AE')
    || phoneCountries[0];
}

const COUNTRY_CURRENCY_BY_SLUG = {
  ca: 'CAD',
  ae: 'AED',
};

const COUNTRY_CURRENCY_BY_CODE = {
  CA: 'CAD',
  AE: 'AED',
};

/** Currency code for navbar country (fallback when service unit is missing). */
export function resolveCurrencyForCountry(countryRecord, fallbackSlug = 'ca') {
  const slug = (
    countryRecord?.slug
    || currentCountrySlug.value
    || readCountrySlugFromStorage()
    || fallbackSlug
  ).toString().toLowerCase().trim();

  if (COUNTRY_CURRENCY_BY_SLUG[slug]) {
    return COUNTRY_CURRENCY_BY_SLUG[slug];
  }

  const code = (
    countryRecord?.code
    || readSelectedCountryCodeFromStorage()
    || ''
  ).toString().trim().toUpperCase();

  if (COUNTRY_CURRENCY_BY_CODE[code]) {
    return COUNTRY_CURRENCY_BY_CODE[code];
  }

  return slug === 'ae' ? 'AED' : 'CAD';
}

export function normalizeCurrencyUnit(unit) {
  return (unit || '').toString().trim();
}

